import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";
import { FileText, Mail, MessageSquare, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import { InvoiceRecipientChipInput } from "@/modules/billing/InvoiceRecipientChipInput";
import { formatInvoiceDueDate } from "@/modules/billing/invoiceEmailTemplate";
import { formatUsPhoneDisplayFromAny } from "@/utils/phone";
import { cn } from "@/lib/utils";

/** Label above a form field (Xero-style stacked form). */
const Field = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label
      htmlFor={htmlFor}
      className="text-xs font-medium text-muted-foreground"
    >
      {label}
    </Label>
    {children}
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

const previewFrameClass = "mt-0 overflow-hidden rounded-xl border bg-card";

/** Full-size, sandboxed render of an email body (scrolls inside). */
const EmailHtmlPreview = ({ html, title }: { html: string; title: string }) => (
  <iframe
    title={title}
    srcDoc={html}
    sandbox=""
    className="block h-[560px] w-full border-0 bg-[#f3f4f6]"
  />
);

/** Inbox-style header above a previewed email. */
const EmailEnvelope = ({
  subject,
  from,
  to,
}: {
  subject: string;
  from: string;
  to: string;
}) => (
  <div className="space-y-1 border-b bg-card px-4 py-3">
    <p className="truncate text-sm font-semibold">
      {subject || "(no subject)"}
    </p>
    <p className="truncate text-xs text-muted-foreground">
      <span className="text-foreground">From</span> {from}
    </p>
    <p className="truncate text-xs text-muted-foreground">
      <span className="text-foreground">To</span> {to || "—"}
    </p>
  </div>
);

/** Sender identity for the From rows (billing email sender, SMS number). */
const useSendIdentity = (organizationName: string) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: email } = useQuery({
    queryKey: ["email-delivery-settings"],
    queryFn: () => dataProvider.getEmailDeliverySettings(),
    retry: false,
    staleTime: 300_000,
  });
  const { data: messaging } = useQuery({
    queryKey: ["messaging-settings"],
    queryFn: () => dataProvider.getMessagingSettings(),
    retry: false,
    staleTime: 30_000,
  });
  const billingFrom = email?.billing_from_email?.trim() || email?.billing_from;
  const smsNumber =
    messaging?.messaging_provider === "telnyx"
      ? messaging?.telnyx_phone_number
      : messaging?.twilio_phone_number;
  return {
    emailFrom: billingFrom
      ? `${organizationName} <${billingFrom}>`
      : organizationName,
    smsFrom: smsNumber
      ? `${organizationName} · ${formatUsPhoneDisplayFromAny(smsNumber)}`
      : organizationName,
  };
};

/**
 * Send step shared by every invoice send dialog (ticket, combined tickets,
 * direct invoice). Left: what is sent and to whom (To, Subject, Message, text
 * message). Right: tabs that show exactly what the client receives — the
 * email, the invoice PDF, the after-payment email and the text message.
 */
export const InvoiceSendStep = ({
  idPrefix,
  organizationName,
  amountFormatted,
  dueDate,
  deliverySummary,
  company,
  contact,
  toEmails,
  onToEmailsChange,
  subject,
  onSubjectChange,
  emailMessage,
  onEmailMessageChange,
  paymentEmailHtml,
  deliverySubject,
  deliveryEmailHtml,
  fileCount,
  sendSms,
  onSendSmsChange,
  phones,
  onPhonesChange,
  smsText,
  invoiceDocument,
  sendEmail = true,
  onSendEmailChange,
  emailExtras,
  attachmentNote,
  paymentChoice,
  sendPlan,
}: {
  idPrefix: string;
  organizationName: string;
  amountFormatted: string;
  dueDate: string;
  /** After-payment automation (ticket file delivery); hidden when omitted. */
  deliverySummary?: string;
  company?: Company | null;
  contact?: Contact | null;
  toEmails: string[];
  onToEmailsChange: (next: string[]) => void;
  subject: string;
  onSubjectChange: (next: string) => void;
  emailMessage: string;
  onEmailMessageChange: (next: string) => void;
  paymentEmailHtml: string | null;
  deliverySubject?: string;
  deliveryEmailHtml?: string | null;
  fileCount?: number;
  sendSms: boolean;
  onSendSmsChange: (next: boolean) => void;
  phones: string[];
  onPhonesChange: (next: string[]) => void;
  smsText: string;
  /** The invoice document (same as step 1), shown in the PDF tab. */
  invoiceDocument?: ReactNode;
  /** Email on/off; the toggle only shows when onSendEmailChange is set. */
  sendEmail?: boolean;
  onSendEmailChange?: (next: boolean) => void;
  /** Extra recipient fields under To (e.g. Cc / Bcc). */
  emailExtras?: ReactNode;
  /** Footer under the email preview (e.g. "INV-1.pdf attached"). */
  attachmentNote?: string;
  /** "How will this be paid?" (send the invoice or charge a saved method). */
  paymentChoice?: ReactNode;
  /** Step-by-step "what happens when you click Send"; replaces the after-payment note. */
  sendPlan?: (identity: { emailFrom: string; smsFrom: string }) => ReactNode;
}) => {
  const { emailFrom, smsFrom } = useSendIdentity(organizationName);
  const toLine = toEmails.join(", ");
  const tabs = [
    ...(sendEmail ? ["email"] : []),
    ...(invoiceDocument ? ["pdf"] : []),
    ...(deliveryEmailHtml ? ["delivery"] : []),
    ...(sendSms ? ["sms"] : []),
  ];
  const [selectedTab, setSelectedTab] = useState("email");
  const activeTab = tabs.includes(selectedTab) ? selectedTab : tabs[0];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start">
      {/* What is sent and to whom */}
      <div className="space-y-4">
        {paymentChoice}
        {onSendEmailChange ? (
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5">
            <Mail className="size-4 text-muted-foreground" />
            <Label
              htmlFor={`${idPrefix}-email`}
              className="cursor-pointer text-sm font-medium"
            >
              Send by email
            </Label>
            <Switch
              id={`${idPrefix}-email`}
              className="ml-auto"
              checked={sendEmail}
              onCheckedChange={onSendEmailChange}
            />
          </div>
        ) : null}

        {sendEmail ? (
          <>
            <Field label="From">
              <p className="truncate rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {emailFrom}
              </p>
            </Field>
            <Field label="To" htmlFor={`${idPrefix}-to`}>
              <InvoiceRecipientChipInput
                hideLabel
                company={company}
                contact={contact}
                id={`${idPrefix}-to`}
                label="To"
                mode="email"
                values={toEmails}
                onChange={onToEmailsChange}
                placeholder="client@example.com"
              />
            </Field>
            {emailExtras}
            <Field label="Subject" htmlFor={`${idPrefix}-subject`}>
              <Input
                id={`${idPrefix}-subject`}
                value={subject}
                onChange={(event) => onSubjectChange(event.target.value)}
                placeholder="Subject"
              />
            </Field>
            <Field
              label="Message"
              htmlFor={`${idPrefix}-message`}
              hint="The amount, due date, invoice details and Pay button are added around your message."
            >
              <Textarea
                id={`${idPrefix}-message`}
                value={emailMessage}
                onChange={(event) => onEmailMessageChange(event.target.value)}
                rows={7}
                className="min-h-36 resize-y"
                placeholder="Write a message…"
              />
            </Field>
          </>
        ) : null}

        <div className="rounded-lg border">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <MessageSquare className="size-4 text-muted-foreground" />
            <Label
              htmlFor={`${idPrefix}-sms`}
              className="cursor-pointer text-sm font-medium"
            >
              Also send a text message
            </Label>
            <Switch
              id={`${idPrefix}-sms`}
              className="ml-auto"
              checked={sendSms}
              onCheckedChange={onSendSmsChange}
            />
          </div>
          {sendSms ? (
            <div className="space-y-1.5 border-t px-3 py-3">
              <p className="truncate text-xs text-muted-foreground">
                From {smsFrom}
              </p>
              <InvoiceRecipientChipInput
                hideLabel
                company={company}
                contact={contact}
                id={`${idPrefix}-phones`}
                label="Text message to"
                mode="phone"
                values={phones}
                onChange={onPhonesChange}
                placeholder="(203) 555-0100"
              />
            </div>
          ) : null}
        </div>

        {sendPlan ? (
          sendPlan({ emailFrom, smsFrom })
        ) : deliverySummary ? (
          <div className="flex items-start gap-2 rounded-lg bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
            <Package className="mt-0.5 size-4 shrink-0" />
            <p>
              <span className="font-medium text-foreground">
                After payment:
              </span>{" "}
              {deliverySummary} automatically by email.
            </p>
          </div>
        ) : null}
      </div>

      {/* What the client receives */}
      <Tabs value={activeTab} onValueChange={setSelectedTab} className="gap-3">
        <TabsList>
          {sendEmail ? (
            <TabsTrigger value="email">
              <Mail className="size-3.5" />
              Email
            </TabsTrigger>
          ) : null}
          {invoiceDocument ? (
            <TabsTrigger value="pdf">
              <FileText className="size-3.5" />
              Invoice PDF
            </TabsTrigger>
          ) : null}
          {deliveryEmailHtml ? (
            <TabsTrigger value="delivery">
              <Package className="size-3.5" />
              After payment
            </TabsTrigger>
          ) : null}
          {sendSms ? (
            <TabsTrigger value="sms">
              <MessageSquare className="size-3.5" />
              Text
            </TabsTrigger>
          ) : null}
        </TabsList>

        {sendEmail ? (
          <TabsContent value="email" className={previewFrameClass}>
            <EmailEnvelope subject={subject} from={emailFrom} to={toLine} />
            {paymentEmailHtml ? (
              <EmailHtmlPreview
                html={paymentEmailHtml}
                title="Invoice email preview"
              />
            ) : (
              <p className="p-6 text-sm text-muted-foreground">
                The email preview appears once the payment link is ready.
              </p>
            )}
            {attachmentNote ? (
              <p className="flex items-center gap-1.5 border-t px-4 py-2.5 text-xs text-muted-foreground">
                <FileText className="size-3.5" />
                {attachmentNote}
              </p>
            ) : null}
          </TabsContent>
        ) : null}

        {invoiceDocument ? (
          <TabsContent
            value="pdf"
            className={cn(
              previewFrameClass,
              "max-h-[640px] overflow-auto bg-muted/40 p-4",
            )}
          >
            {invoiceDocument}
          </TabsContent>
        ) : null}

        {deliveryEmailHtml ? (
          <TabsContent value="delivery" className={previewFrameClass}>
            <EmailEnvelope
              subject={`${deliverySubject || "Your files are ready"} · ${fileCount ?? 0} file${fileCount === 1 ? "" : "s"}`}
              from={emailFrom}
              to={toLine}
            />
            <EmailHtmlPreview
              html={deliveryEmailHtml}
              title="Delivery email preview"
            />
            <p className="border-t px-4 py-2.5 text-xs text-muted-foreground">
              Sent automatically to the same recipients when the client pays
              (due {formatInvoiceDueDate(dueDate)} · {amountFormatted}).
            </p>
          </TabsContent>
        ) : null}

        {sendSms ? (
          <TabsContent value="sms" className={cn(previewFrameClass, "p-4")}>
            <p className="mb-3 text-xs text-muted-foreground">
              From {smsFrom} · To{" "}
              {phones.map(formatUsPhoneDisplayFromAny).join(", ") || "—"}
            </p>
            <div className="max-w-sm rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm whitespace-pre-line">
              {smsText || "—"}
            </div>
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
};
